import React, { Suspense, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container } from "react-bootstrap";

// redux
import { RootState, AppDispatch } from "../redux/store";
import { LayoutTypes, SideBarTypes } from "../constants/layout";
import { changeSidebarType } from "../redux/actions";

// utils
import { changeHTMLAttribute } from "../utils";
import { useViewport } from "../hooks/useViewPort";
import { PageTitleProvider } from "../contexts/PageTitleContext";
import { PageTabsProvider } from "../contexts/PageTabsContext";
import { MenuBadgesProvider } from "../contexts/MenuBadgesContext";

// code splitting and lazy loading
const Topbar = React.lazy(() => import("./Topbar"));
const LeftSidebar = React.lazy(() => import("./LeftSidebar"));
const RightSidebar = React.lazy(() => import("./RightSidebar"));
const PageTabsBar = React.lazy(() => import("./PageTabsBar"));

const loading = () => <div className=""></div>;

interface VerticalLayoutProps {
  children?: any;
}

const VerticalLayout = ({ children }: VerticalLayoutProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { width } = useViewport();

  const {
    layoutMode,
    layoutColor,
    layoutWidth,
    menuPosition,
    leftSideBarTheme,
    leftSideBarType,
    showSidebarUserInfo,
    showTwoToneIcons,
    topbarTheme,
    isOpenRightSideBar,
  } = useSelector((state: RootState) => ({
    layoutMode: state.Layout.layoutMode,
    layoutColor: state.Layout.layoutColor,
    layoutWidth: state.Layout.layoutWidth,
    menuPosition: state.Layout.menuPosition,
    leftSideBarTheme: state.Layout.leftSideBarTheme,
    leftSideBarType: state.Layout.leftSideBarType,
    showSidebarUserInfo: state.Layout.showSidebarUserInfo,
    showTwoToneIcons: state.Layout.showTwoToneIcons,
    topbarTheme: state.Layout.topbarTheme,
    isOpenRightSideBar: state.Layout.isOpenRightSideBar,
  }));

  const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

  /*
  layout defaults
  */

  useEffect(() => {
    if (width < 1140) {
      dispatch(changeSidebarType(SideBarTypes.LEFT_SIDEBAR_TYPE_FULL));
      // document.getElementsByTagName('html')[0].classList.add('sidebar-enable')
    } else if (width >= 1140) {
      dispatch(changeSidebarType(SideBarTypes.LEFT_SIDEBAR_TYPE_DEFAULT));
      document
        .getElementsByTagName("html")[0]
        .classList.remove("sidebar-enable");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  useEffect(() => {
    changeHTMLAttribute("data-layout", LayoutTypes.LAYOUT_VERTICAL);
  }, []);

  useEffect(() => {
    changeHTMLAttribute("data-layout-mode", String(layoutMode));
  }, [layoutMode]);

  useEffect(() => {
    changeHTMLAttribute("data-bs-theme", String(layoutColor));
  }, [layoutColor]);

  useEffect(() => {
    changeHTMLAttribute("data-layout-width", String(layoutWidth));
  }, [dispatch, layoutWidth]);

  useEffect(() => {
    changeHTMLAttribute("data-menu-position", String(menuPosition));
  }, [menuPosition]);

  useEffect(() => {
    changeHTMLAttribute("data-menu-color", String(leftSideBarTheme));
  }, [leftSideBarTheme]);

  useEffect(() => {
    changeHTMLAttribute("data-sidenav-size", String(leftSideBarType));
  }, [leftSideBarType]);

  useEffect(() => {
    changeHTMLAttribute("data-topbar-color", String(topbarTheme));
  }, [topbarTheme]);

  useEffect(() => {
    changeHTMLAttribute(
      "data-menu-icon",
      showTwoToneIcons ? "twotones" : "default"
    );
  }, [showTwoToneIcons]);

  useEffect(() => {
    changeHTMLAttribute("data-sidenav-user", String(showSidebarUserInfo));
  }, [showSidebarUserInfo]);

  /**
   * Open the menu when having mobile screen
   */
  const openMenu = () => {
    setIsMenuOpened((prevState) => !prevState);

    if (document.body) {
      if (isMenuOpened) {
        document.body.classList.remove("sidebar-enable");
      } else {
        document.body.classList.add("sidebar-enable");
      }
    }
  };

  const isCondensed: boolean =
    leftSideBarType === SideBarTypes.LEFT_SIDEBAR_TYPE_CONDENSED;

  return (
    <>
      <MenuBadgesProvider>
        <div id="wrapper">
          <Suspense fallback={loading()}>
            <LeftSidebar isCondensed={isCondensed} hideLogo={false} />
          </Suspense>

          <div className="content-page moga-content-modern">
            <PageTitleProvider>
              <PageTabsProvider>
                <Suspense fallback={loading()}>
                  <Topbar openLeftMenuCallBack={openMenu} />
                </Suspense>

                <div className="content">
                  <Suspense fallback={loading()}>
                    <PageTabsBar />
                  </Suspense>
                  <Container fluid>
                    <Suspense fallback={loading()}>
                      <div className="moga-pages-wrapper">{children}</div>
                    </Suspense>
                  </Container>
                </div>
              </PageTabsProvider>
            </PageTitleProvider>

            {/* <Suspense fallback={loading()}>
              <Footer />
            </Suspense> */}
          </div>
        </div>
        {isOpenRightSideBar && (
          <Suspense fallback={loading()}>
            <RightSidebar />
          </Suspense>
        )}
      </MenuBadgesProvider>
    </>
  );
};
export default VerticalLayout;
